import React, { useState } from 'react';

import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import toast from 'react-hot-toast';
import ScrollableDropdown from './ScrollableDropdown';

const TransferOwnershipModal = ({ isOpen, onClose, applicationId, teamMembers, currentOwnerId, onSuccess }) => {
    const [selectedMember, setSelectedMember] = useState('');
    const [loading, setLoading] = useState(false);

    // Filter out current owner and non-developers from selectable members
    // Requirement 2: Display ONLY team members who are Developers (DEV)
    const eligibleMembers = teamMembers.filter(member => {
        // Skip if userId is null or undefined
        if (!member.userId) return false;

        const memberId = member.userId._id || member.userId;
        // Check if member is NOT the current owner AND has role 'DEV'
        // Also handle object vs string role checks if necessary, but 'DEV' is the standard token
        return memberId !== currentOwnerId && member.role === 'DEV';
    });

    const handleTransfer = async () => {
        if (!selectedMember) return;

        if (!window.confirm('Are you sure you want to transfer ownership? You will lose owner privileges immediately.')) {
            return;
        }

        setLoading(true);
        try {
            await axios.put(`/api/applications/${applicationId}/transfer-ownership`, {
                newOwnerId: selectedMember
            });
            toast.success('Ownership transferred successfully');
            onSuccess();
            onClose();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to transfer ownership');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                    <div className="absolute inset-0 bg-gray-500 opacity-75" onClick={onClose}></div>
                </div>

                <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

                <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                    <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                        <div className="sm:flex sm:items-start">
                            <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                                <ExclamationTriangleIcon className="h-6 w-6 text-red-600" aria-hidden="true" />
                            </div>
                            <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                                <h3 className="text-lg leading-6 font-medium text-gray-900">
                                    Transfer Ownership
                                </h3>
                                <div className="mt-2">
                                    <p className="text-sm text-gray-500 mb-4">
                                        Select a team member to transfer ownership to. This action cannot be undone by you.
                                    </p>

                                    {eligibleMembers.length === 0 ? (
                                        <p className="text-sm text-yellow-600 bg-yellow-50 p-2 rounded">
                                            No other developers found in the team. Add them first.
                                        </p>
                                    ) : (
                                        <div className="mt-1">
                                            <ScrollableDropdown
                                                value={selectedMember}
                                                onChange={(value) => setSelectedMember(value)}
                                                options={[
                                                    { value: '', label: 'Select a new owner' },
                                                    ...eligibleMembers.map((member) => ({
                                                        value: member.userId?._id || member.userId,
                                                        label: `${member.userId?.name || 'Unknown'} (DEV)`
                                                    }))
                                                ]}
                                                placeholder="Select a new owner"
                                                buttonHeightClassName="h-10"
                                                maxMenuHeightPx={240}
                                                maxMenuHeightClassName="max-h-60"
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                        <button
                            type="button"
                            disabled={!selectedMember || loading}
                            onClick={handleTransfer}
                            className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm ${(!selectedMember || loading) ? 'opacity-50 cursor-not-allowed' : ''
                                }`}
                        >
                            {loading ? 'Transferring...' : 'Transfer Ownership'}
                        </button>
                        <button
                            type="button"
                            onClick={onClose}
                            className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TransferOwnershipModal;
